import { type PropsWithChildren, createContext, useContext } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { useCheckConsentQuery } from '@/apis/accounts/slice'
import { useGetInstanceQuery } from '@/apis/apps/slice'
import type {
  IApplication,
  IApplicationOnboarding,
} from '@/apis/apps/interfaces'
import {
  isAppInstalled,
  checkIfIsService,
  hasDirectInstallUrl,
  getItemOnboarding,
  getItemClientId,
  canInstallApplications,
} from '@/store/selectors'
import useParamSelector from '@/hooks/useParamSelector'
import { APP_CONFIG_STATUS } from '@/constants'
import { useTypedSelector } from '@/store/hooks'

interface IOnboardingProviderProps extends PropsWithChildren {
  id: IApplication['id']
}

interface IOnboardingContext {
  checkOnboarding: () => void
  isFetchingOnboarding: boolean
  fetchingOnboardingError: unknown
  isLoadingOnboarding: boolean
  isInstalled: boolean
  requireConsent: boolean
  hasAcceptedConsent: boolean
  requireConfiguration: boolean
  isConfigured: boolean
  introduction: IApplicationOnboarding['introduction']
  settings: IApplicationOnboarding['settings']
  tutorial: IApplicationOnboarding['tutorial']
}

const OnboardingContext = createContext<IOnboardingContext | undefined>(
  undefined
)

export const OnboardingProvider = ({
  id,
  children,
}: IOnboardingProviderProps) => {
  const clientId = useParamSelector(getItemClientId, id)
  const isInstalled = useParamSelector(isAppInstalled, id)
  const isService = useParamSelector(checkIfIsService, id)
  const hasDirectInstallUrlFlow = !!useParamSelector(hasDirectInstallUrl, id)
  const onboarding = useParamSelector(getItemOnboarding, id)
  const canInstallApplication = useTypedSelector(canInstallApplications)

  const {
    introduction = '',
    settings = '',
    tutorial = '',
    setupAware,
  } = onboarding || {}

  const enableFetching = isInstalled && !isService
  const requireConsent = hasDirectInstallUrlFlow
  const requireConfiguration = hasDirectInstallUrlFlow && !!setupAware

  const {
    data: consent,
    error: consentError,
    isFetching: isFetchingConsent,
    isLoading: isLoadingConsent,
    refetch: refetchConsent,
  } = useCheckConsentQuery(clientId ?? skipToken, {
    skip: !(enableFetching && requireConsent),
  })

  const {
    data: instance,
    error: instanceError,
    isFetching: isFetchingInstance,
    isLoading: isLoadingInstance,
    refetch: refetchInstance,
  } = useGetInstanceQuery(id, {
    skip: !(enableFetching && requireConfiguration),
  })

  const hasAcceptedConsent = !requireConsent || !!consent?.consent_granted
  // isConfigured - if the configuration status is either 'unregistered' or 'completed', consider the configuration finished
  const isConfigured =
    !requireConfiguration ||
    instance?.setupStatus !== APP_CONFIG_STATUS.UNCOMPLETED

  const fetchingOnboardingError = consentError || instanceError
  const isFetchingOnboarding = isFetchingConsent || isFetchingInstance
  const isLoadingOnboarding = isLoadingConsent || isLoadingInstance

  const checkOnboarding = () => {
    if (canInstallApplication) {
      if (requireConfiguration) {
        refetchInstance()
      }

      if (requireConsent) {
        refetchConsent()
      }
    }
  }

  return (
    <OnboardingContext.Provider
      value={{
        checkOnboarding,
        isFetchingOnboarding,
        fetchingOnboardingError,
        isLoadingOnboarding,
        isInstalled,
        requireConsent,
        hasAcceptedConsent,
        requireConfiguration,
        isConfigured,
        introduction,
        settings,
        tutorial,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export const useOnboarding = () => {
  const context = useContext(OnboardingContext)

  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider')
  }

  return context
}
